import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';
import ImageGallery from '../../components/ImageGallery';

export default function VerPropiedadAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [propiedad, setPropiedad] = useState<any>(null);
  const [inmobiliaria, setInmobiliaria] = useState<any>(null);
  const [asesores, setAsesores] = useState<any[]>([]);
  const [proyecto, setProyecto] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        // ✅ Cargar propiedad usando doc.id (el id de la URL es el docId de Firestore)
        const propRef = doc(db, 'propiedades', id);
        const propSnap = await getDoc(propRef);

        if (!propSnap.exists()) {
          Swal.fire({
            icon: 'error',
            title: 'No encontrada',
            text: 'La propiedad no existe.'
          });
          return navigate('/admin-property');
        }

        const propData = propSnap.data();
        setPropiedad({ docId: propSnap.id, ...propData });

        // ✅ Verificar permisos usando inmobiliaria_doc_id
        const esAdmin = user?.tipo?.includes('admin');
        const esAsociado = user?.tipo?.includes('asociado') &&
                           propData.inmobiliaria_doc_id === user.inmobiliaria_doc_id;

        if (!esAdmin && !esAsociado) {
          Swal.fire({
            icon: 'error',
            title: 'Acceso denegado',
            text: 'No tienes permiso para ver esta propiedad.'
          });
          return navigate('/admin-property');
        }

        // ✅ Cargar inmobiliaria usando inmobiliaria_doc_id
        if (propData.inmobiliaria_doc_id) {
          try {
            const inmoRef = doc(db, 'inmobiliaria', propData.inmobiliaria_doc_id);
            const inmoSnap = await getDoc(inmoRef);
            if (inmoSnap.exists()) {
              setInmobiliaria({ id: inmoSnap.id, ...inmoSnap.data() });
            }
          } catch (error) {
            console.error('Error cargando inmobiliaria:', error);
          }
        }

        // ✅ Cargar asesores usando asesor_doc_ids
        if (propData.asesor_doc_ids && Array.isArray(propData.asesor_doc_ids)) {
          const asesoresData: any[] = [];

          for (const docId of propData.asesor_doc_ids) {
            try {
              const asesorRef = doc(db, 'asesor', docId);
              const asesorSnap = await getDoc(asesorRef);
              if (asesorSnap.exists()) {
                asesoresData.push({ id: asesorSnap.id, ...asesorSnap.data() });
              }
            } catch (error) {
              console.error('Error cargando asesor:', error);
            }
          }

          setAsesores(asesoresData);
        }

        // ✅ Cargar proyecto si existe proyecto_doc_id
        if (propData.proyecto_doc_id) {
          try {
            const proyectoRef = doc(db, 'proyectos', propData.proyecto_doc_id);
            const proyectoSnap = await getDoc(proyectoRef);
            if (proyectoSnap.exists()) {
              setProyecto({ id: proyectoSnap.id, ...proyectoSnap.data() });
            }
          } catch (error) {
            console.error('Error cargando proyecto:', error);
          }
        }

      } catch (error) {
        console.error('Error cargando datos:', error);
        Swal.fire('Error', 'No se pudo cargar la información de la propiedad.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user, navigate]);

  const eliminarPropiedad = async () => {
    const result = await Swal.fire({
      title: '¿Eliminar esta propiedad?',
      text: 'Esta acción es permanente y no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!result.isConfirmed) return;

    try {
      // ✅ Eliminar directamente usando el doc.id
      await deleteDoc(doc(db, 'propiedades', id!));

      Swal.fire({
        icon: 'success',
        title: 'Eliminada',
        text: 'La propiedad fue eliminada correctamente.'
      });

      navigate('/admin-property');
    } catch (error) {
      console.error('Error eliminando propiedad:', error);
      Swal.fire('Error', 'No se pudo eliminar la propiedad.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando información de la propiedad...</p>
        </div>
      </div>
    );
  }

  if (!propiedad) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <h4>Propiedad no encontrada</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/admin-property')}>
            Volver a propiedades
          </button>
        </div>
      </div>
    );
  }

  const formatPrice = () => {
    if (propiedad.precio_actual_usd && propiedad.precio_actual_usd > 0) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(propiedad.precio_actual_usd);
    } else if (propiedad.precio_actual_mxn && propiedad.precio_actual_mxn > 0) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0
      }).format(propiedad.precio_actual_mxn);
    }
    return 'No definido';
  };

  const getEstatusColor = (estatus: string) => {
    switch (estatus?.toLowerCase()) {
      case 'disponible':
      case 'activo':
      case 'a':
        return 'success';
      case 'vendida':
      case 'vendido':
        return 'danger';
      case 'pendiente':
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const nombrePropiedad = propiedad.titulo || propiedad.nombre || `Propiedad ${propiedad.list_number || 'Sin título'}`;
  const estatus = propiedad.estado || propiedad.estatus || 'Sin definir';

  return (
    <>
      <UserNav />
      <div className="container mt-5 mb-5">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Administración de Propiedades', path: '/admin-property' },
            { label: nombrePropiedad }
          ]}
        />

        {/* Header con título y acciones */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h2 className="mb-3">
                  <i className="fas fa-home me-2 text-primary"></i>
                  {nombrePropiedad}
                </h2>
                <div className="d-flex gap-2 flex-wrap">
                  <span className={`badge bg-${getEstatusColor(estatus)}`}>
                    {estatus}
                  </span>
                  <span className={`badge ${propiedad.activo !== false ? 'bg-success' : 'bg-secondary'}`}>
                    {propiedad.activo !== false ? 'Activa' : 'Inactiva'}
                  </span>
                  {propiedad.estatus_publicacion && (
                    <span className="badge bg-info">
                      Publicación: {propiedad.estatus_publicacion}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-end mt-3 mt-md-0">
                <div className="btn-group-vertical d-grid gap-2">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => navigate(`/single-property-1/${id}`)}
                  >
                    <i className="fas fa-eye me-2"></i>Ver Vista Pública
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={() => navigate(`/edit-property/${id}`)}
                  >
                    <i className="fas fa-edit me-2"></i>Editar Propiedad
                  </button>
                  {user?.tipo?.includes('admin') && (
                    <button
                      className="btn btn-danger"
                      onClick={eliminarPropiedad}
                    >
                      <i className="fas fa-trash me-2"></i>Eliminar Propiedad
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Información General */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0"><i className="fas fa-info-circle me-2"></i>Información General</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-dollar-sign me-2 text-success"></i>Precio:</strong>
                    <div className="text-success fw-bold">{formatPrice()}</div>
                  </div>
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-hashtag me-2 text-muted"></i>ID:</strong>
                    <div><code>{propiedad.propiedad_id || propiedad.list_number || 'Sin ID'}</code></div>
                  </div>
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-bed me-2 text-primary"></i>Recámaras:</strong>
                    <div>{propiedad.recamaras || 0}</div>
                  </div>
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-bath me-2 text-primary"></i>Baños:</strong>
                    <div>{propiedad.banos_completos || 0} completos</div>
                  </div>
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-car me-2 text-info"></i>Estacionamiento:</strong>
                    <div>{propiedad.estacionamientos || 0}</div>
                  </div>
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-ruler-combined me-2 text-warning"></i>M² Total:</strong>
                    <div>{propiedad.m2_total || propiedad.construccion_m2 || 0} m²</div>
                  </div>
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-expand-arrows-alt me-2 text-warning"></i>M² Lote:</strong>
                    <div>{propiedad.m2_lote || propiedad.terreno_m2 || 0} m²</div>
                  </div>
                  <div className="col-6 mb-3">
                    <strong><i className="fas fa-calendar me-2 text-secondary"></i>Fecha Registro:</strong>
                    <div className="small">
                      {propiedad.fecha_creacion?.seconds
                        ? new Date(propiedad.fecha_creacion.seconds * 1000).toLocaleDateString('es-MX')
                        : 'No disponible'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0"><i className="fas fa-map-marker-alt me-2"></i>Ubicación y Enlaces</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <strong><i className="fas fa-map-pin me-2 text-danger"></i>Dirección:</strong>
                  <div>
                    {propiedad.direccion?.area || propiedad.direccion?.zona || propiedad.ciudad || 'No especificada'}
                  </div>
                  {propiedad.direccion?.calle && (
                    <div className="small text-muted">{propiedad.direccion.calle}</div>
                  )}
                </div>

                {propiedad.direccion?.coordinates && (
                  <div className="mb-3">
                    <a
                      href={`https://www.google.com/maps?q=${propiedad.direccion.coordinates.lat},${propiedad.direccion.coordinates.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="fas fa-map-marked-alt me-2"></i>Ver en Google Maps
                    </a>
                  </div>
                )}

                {propiedad.flex_url && (
                  <div className="mb-3">
                    <strong><i className="fas fa-link me-2 text-info"></i>FlexMLS:</strong>
                    <div>
                      <a href={propiedad.flex_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-info">
                        <i className="fas fa-external-link-alt me-2"></i>Ver en FlexMLS
                      </a>
                    </div>
                  </div>
                )}

                {inmobiliaria && (
                  <div className="mb-3">
                    <strong><i className="fas fa-building me-2 text-primary"></i>Inmobiliaria:</strong>
                    <div>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/ver-inm/${inmobiliaria.id}`)}
                      >
                        {inmobiliaria.nombre}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Descripción */}
        {propiedad.descripcion && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0"><i className="fas fa-align-left me-2"></i>Descripción</h5>
            </div>
            <div className="card-body">
              <p className="mb-0">{propiedad.descripcion}</p>
            </div>
          </div>
        )}

        {/* Galería de Fotos */}
        <div className="card mb-4 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0"><i className="fas fa-images me-2"></i>Galería de Fotos</h5>
          </div>
          <div className="card-body">
            {propiedad.fotos && propiedad.fotos.length > 0 ? (
              <ImageGallery
                images={propiedad.fotos}
                showThumbnails={true}
                thumbnailSize="lg"
                lightbox={true}
                className="admin-property-gallery"
              />
            ) : (
              <div className="alert alert-info mb-0">
                <i className="fa fa-info-circle me-2"></i>
                No hay fotos disponibles para esta propiedad.
                <button
                  className="btn btn-outline-primary btn-sm ms-2"
                  onClick={() => navigate(`/edit-property/${id}`)}
                >
                  <i className="fas fa-plus me-1"></i>Agregar fotos
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Amenidades */}
        {propiedad.amenidades && propiedad.amenidades.length > 0 && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0"><i className="fas fa-star me-2"></i>Amenidades</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {propiedad.amenidades.map((amenidad: string, index: number) => (
                  <div key={index} className="col-md-4 col-sm-6 mb-2">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    {amenidad}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Asesores Asignados */}
        {asesores.length > 0 && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0"><i className="fas fa-user-tie me-2"></i>Asesores Asignados ({asesores.length})</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {asesores.map((asesor) => (
                  <div key={asesor.id} className="col-md-6 mb-3">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title">{asesor.nombre}</h6>
                        <p className="card-text small text-muted mb-2">
                          <i className="fas fa-envelope me-1"></i>{asesor.correo || 'Sin correo'}
                        </p>
                        <p className="card-text small text-muted mb-2">
                          <i className="fas fa-phone me-1"></i>{asesor.telefono || 'Sin teléfono'}
                        </p>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/ver-admin-afiliado/${asesor.id}`)}
                        >
                          <i className="fas fa-eye me-1"></i>Ver perfil
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Proyecto Asociado */}
        {proyecto && (
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0"><i className="fas fa-project-diagram me-2"></i>Proyecto Asociado</h5>
            </div>
            <div className="card-body">
              <h6>{proyecto.nombre}</h6>
              <p className="text-muted">{proyecto.descripcion?.slice(0, 150)}...</p>
              <button
                className="btn btn-outline-primary"
                onClick={() => navigate(`/ver-project/${proyecto.id}`)}
              >
                <i className="fas fa-eye me-2"></i>Ver proyecto completo
              </button>
            </div>
          </div>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
